import CreativeGridView from '@/Enums/CampaignManagement/CreativeGridView.enum';
import CreativeType from '@/Enums/Creatives/CreativeType.enum';
import { IDV360TicketsCreative } from '@/Interfaces/DV360Tickets/Creative.interface';
import { IProduct } from '@/Interfaces/Product.interface';
import { GridPaginationAndSortFilters } from '@/components/Grid/hooks/useGridApiPaginationAndSort';
import ModalBody from '@/components/Modal/common/ModalBody';
import { getAdvertiserControlOption } from '@/helpers/advertisers';
import { getColumns } from '@/pages/CampaignManagement/CreateCampaign/DV360TicketsLineItemForm/Creatives/Table/columns';
import CreativesModalButtons from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/Creatives/Modal/components/CreativesModalButtons';
import CreativesModalHeader from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/Creatives/Modal/components/CreativesModalHeader';
import CreativesModalSearch from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/Creatives/Modal/components/CreativesModalSearch';
import CreativesTable from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/Creatives/Table';
import { ICreativeFormModalDefaults } from '@/pages/CreativeLibrary/Modals/CreativeFormModal';
import DV360CreativeFormModal from '@/pages/CreativeLibrary/Modals/DV360CreativeFormModal';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectDV360TicketsCreatives,
  selectDV360TicketsCreativesFilters,
} from '@/store/selectors/dv360Tickets/creatives.selectors';
import { selectDV360TicketsActiveInsertionOrderAdvertiser } from '@/store/selectors/dv360Tickets/insertionOrders.selectors';
import { getDV360Creatives, setFilters } from '@/store/slices/dv360Tickets/creatives.slice';
import { Modal } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import debounce from 'debounce';
import { ChangeEvent, FormEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

interface IProps {
  open: boolean;
  handleClose: () => void;
  associatedCreatives: IDV360TicketsCreative[];
  handleAssociateCreatives: (creatives: IDV360TicketsCreative[]) => void;
  product: IProduct;
}

const typeOptions = [CreativeType.YOUTUBE_LINK, CreativeType.VIDEO];

const DV360TicketsAssociateCreativesModal = memo(
  ({ open, handleClose, associatedCreatives, handleAssociateCreatives, product }: IProps) => {
    const dispatch = useAppDispatch();

    const { creatives, loading, total } = useAppSelector(selectDV360TicketsCreatives);
    const filters = useAppSelector(selectDV360TicketsCreativesFilters);

    const [searchValue, setSearchValue] = useState<string>('');
    const [selectedTypes, setSelectedTypes] = useState<CreativeType[]>(typeOptions);
    const [selectedCreativesIds, setSelectedCreativesIds] = useState<GridRowSelectionModel>([]);
    const [isCreativeFormModalOpen, setIsCreativeFormModalOpen] = useState<boolean>(false);
    const [creativeFormModalDefaults, setCreativeFormModalDefaults] = useState<ICreativeFormModalDefaults>();

    const advertiser = useAppSelector(selectDV360TicketsActiveInsertionOrderAdvertiser);

    const availableCreatives = useMemo(() => {
      if (!associatedCreatives?.length) {
        return creatives;
      }

      const associatedIds = associatedCreatives.map(({ id }) => id);
      return creatives.filter(({ id }) => !associatedIds.includes(id));
    }, [associatedCreatives, creatives]);

    const getCreativesData = useCallback(() => dispatch(getDV360Creatives()), [dispatch]);

    useEffect(() => {
      if (open) {
        getCreativesData();
      }
    }, [filters, getCreativesData, open]);

    const handleSearch = useCallback(
      debounce(newSearch => {
        dispatch(setFilters({ search: newSearch }));
      }, 500),
      [dispatch],
    );

    const handleSearchValueChange = useCallback(
      ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(value);
        handleSearch(value);
      },
      [handleSearch],
    );

    const handleTypeChange = useCallback(
      (value: CreativeType) => {
        const updatedSelectedTypes = selectedTypes.includes(value)
          ? selectedTypes.filter(type => type !== value)
          : [...selectedTypes, value];

        setSelectedTypes(updatedSelectedTypes);
        dispatch(setFilters({ types: updatedSelectedTypes }));
      },
      [dispatch, selectedTypes],
    );

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();
      handleAssociateCreatives(availableCreatives.filter(({ id }) => selectedCreativesIds.includes(id)));
      handleClose();
    };

    const handleOpenCreativeFormModal = useCallback(() => {
      if (advertiser) {
        setCreativeFormModalDefaults({
          advertiser: getAdvertiserControlOption(advertiser),
          types: typeOptions,
        });
      }
      setIsCreativeFormModalOpen(true);
    }, [advertiser]);

    const handleCreativeFormModalClose = useCallback(
      (refresh: boolean = false, duration?: number) => {
        setCreativeFormModalDefaults(undefined);
        setIsCreativeFormModalOpen(false);

        if (duration && duration > (product?.videoDuration || 0)) {
          toast.warn(
            `Creative duration must be less than or equal to product duration (${product?.videoDuration} seconds)`,
          );
        }

        if (refresh) {
          getCreativesData();
        }
      },
      [getCreativesData, product?.videoDuration],
    );

    const columns = useMemo(
      () =>
        getColumns({
          view: CreativeGridView.LineItemModal,
          productLimit: product.videoDuration,
        }),
      [product.videoDuration],
    );

    const setTableFilters = useCallback(
      (filters: GridPaginationAndSortFilters) => {
        dispatch(setFilters(filters));
      },
      [dispatch],
    );

    const isRowSelectable = useCallback(
      (params: any) => {
        if (!product?.videoDuration) {
          return true;
        }

        const duration = (params.row as IDV360TicketsCreative).duration ?? 0;
        return duration <= (product?.videoDuration ?? Infinity);
      },
      [product?.videoDuration],
    );

    return (
      <>
        <Modal open={open}>
          <ModalBody className='!w-[80%] max-w-[1100px]'>
            <CreativesModalHeader handleClose={handleClose} />
            <CreativesModalSearch
              searchValue={searchValue}
              handleSearch={handleSearchValueChange}
              typeOptions={typeOptions}
              selectedTypes={selectedTypes}
              handleTypeChange={handleTypeChange}
              onAddCreativeClick={handleOpenCreativeFormModal}
              onRefreshClick={getCreativesData}
            />
            {loading || availableCreatives?.length ? (
              <form className='mt-[15px]' onSubmit={handleSubmit}>
                <CreativesTable
                  loading={loading}
                  tableData={availableCreatives}
                  rowCount={total}
                  columns={columns}
                  checkboxSelection={true}
                  rowSelectionModel={selectedCreativesIds}
                  handleRowSelectionModelChange={setSelectedCreativesIds}
                  setTableFilters={setTableFilters}
                  isRowSelectable={isRowSelectable}
                />
                <CreativesModalButtons
                  handleClose={handleClose}
                  isSaveDisabled={selectedCreativesIds.length === 0}
                  saveButtonTitle={'Add Selected Creatives'}
                />
              </form>
            ) : (
              <p className='my-20 text-center text-xl font-semibold'>
                {availableCreatives?.length
                  ? 'No Creatives found.'
                  : 'There are no available Creatives. Please add new ones.'}
              </p>
            )}
          </ModalBody>
        </Modal>
        <DV360CreativeFormModal
          open={isCreativeFormModalOpen}
          handleClose={handleCreativeFormModalClose}
          defaults={creativeFormModalDefaults}
        />
      </>
    );
  },
);

export default DV360TicketsAssociateCreativesModal;
